import { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { config } from '_config';
import { theme } from '_constants/theme';

const StyledOuterContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  background-color: ${theme.colors.white};
`;

const StyledInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  position: relative;
  overflow: hidden;

  max-width: ${theme.spaces.large30X * 10}px;
  width: 100%;
`;

const StyledLabel = styled.div`
  padding-left: ${theme.spaces.regular - 2}px;
  padding-right: ${theme.spaces.regular - 2}px;
  padding-top: ${theme.spaces.smallest}px;
  padding-bottom: ${theme.spaces.smallest}px;
  margin-bottom: ${theme.spaces.large}px;

  border-radius: 66px;
  background: #f9f5ff;

  font-family: 'Inter';
  font-size: ${theme.fontSizes.regular}px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.03em;
  color: ${theme.colors.indigo.primary};
`;

const StyledTitle = styled.div`
  margin-bottom: ${theme.spaces.large}px;
  font-family: 'Inter';
  font-size: ${theme.fontSizes.large18X}px;
  font-weight: 600;
  line-height: 83.6px;
  letter-spacing: -0.03em;
  text-align: left;
  background: linear-gradient(270.46deg, #101828 47.44%, #6941c6 102.37%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media ${theme.devices.tablet} {
    font-size: ${theme.fontSizes.large8X}px;
    line-height: 39.6px;
  }
`;

const StyledText = styled.div`
  font-family: 'Inter';
  font-size: ${theme.fontSizes.large}px;
  font-weight: 400;
  line-height: 30px;
  letter-spacing: -0.03em;
  text-align: left;
  color: ${theme.colors.gray.secondary};

  @media ${theme.devices.tablet} {
    font-size: ${theme.fontSizes.medium}px;
    line-height: 27px;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledContentBox = styled(forwardRef(({ fullWidth, ...props }, ref) => <div ref={ref} {...props} />))`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;

  padding-top: ${theme.spaces.large19X}px;
  padding-left: ${theme.spaces.large28X - 1}px;
  padding-right: ${theme.spaces.large28X - 1}px;

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      padding-left: 0;
      padding-right: 0;
    `}

  @media ${theme.devices.tablet} {
    padding-top: ${theme.spaces.large10X}px;
    padding-left: ${theme.spaces.regular}px;
    padding-right: ${theme.spaces.regular}px;

    ${({ fullWidth }) =>
      fullWidth &&
      css`
        padding-left: 0;
        padding-right: 0;
      `}
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledImageContainer = styled(forwardRef(({ absolute, top, bottom, left, right, desktop, mobile, ...props }, ref) => <div ref={ref} {...props} />))`
  ${({ absolute }) =>
    absolute &&
    css`
      position: absolute;
    `}

  ${({ top }) =>
    typeof top !== 'undefined' &&
    css`
      top: ${typeof top === 'string' ? top : `${top}px`};
    `}

  ${({ bottom }) =>
    typeof bottom !== 'undefined' &&
    css`
      bottom: ${typeof bottom === 'string' ? bottom : `${bottom}px`};
    `}

  ${({ left }) =>
    typeof left !== 'undefined' &&
    css`
      left: ${typeof left === 'string' ? left : `${left}px`};
    `}

  ${({ right }) =>
    typeof right !== 'undefined' &&
    css`
      right: ${typeof right === 'string' ? right : `${right}px`};
    `}

  ${({ mobile }) =>
    mobile &&
    css`
      display: none;
    `}

  @media ${theme.devices.tablet} {
    ${({ desktop }) =>
      desktop &&
      css`
        display: none;
      `}

    ${({ mobile }) =>
      mobile &&
      css`
        display: block;
      `}
  }
`;

// eslint-disable-next-line max-len
// eslint-disable-next-line prettier/prettier, react/display-name, @next/next/no-img-element, jsx-a11y/alt-text
const StyledImg = styled(forwardRef(({ height, width, ...props }, ref) => <img ref={ref} {...props} />))`
  ${({ height }) =>
    typeof height !== 'undefined' &&
    css`
      height: ${typeof height === 'string' ? height : `${height}px`};
    `}

  ${({ width }) =>
    typeof width !== 'undefined' &&
    css`
      width: ${typeof width === 'string' ? width : `${width}px`};
    `}
`;

const AboutBannerSection = () => {
  return (
    <StyledOuterContainer>
      <StyledInnerContainer>
        <StyledContentBox>
          <StyledLabel>Our story</StyledLabel>
          <StyledTitle>
            Our journey at Monexa began with a common challenge faced by many
            business owners
          </StyledTitle>
          <StyledText>
            We saw the need for a stress-free and cost-effective solution for
            managing finances. Our mission is to provide small business owners
            with exactly that, driven by the experiences of those who found
            bookkeeping and tax preparation to be overwhelming and costly.
          </StyledText>
          <StyledImageContainer absolute top={0} left={0} right={0}>
            <picture>
              <source
                srcSet={`${config.basePath}/images/shadow.svg`}
                type="image/svg+xml"
              />
              <StyledImg
                src={`${config.basePath}/images/shadow.svg`}
                alt=""
                width="100%"
              />
            </picture>
          </StyledImageContainer>
        </StyledContentBox>
        <StyledContentBox fullWidth>
          <StyledImageContainer desktop>
            <picture>
              <source
                srcSet={`${config.basePath}/images/hands-desktop.png`}
                type="image/png"
              />
              <StyledImg
                src={`${config.basePath}/images/hands-desktop.png`}
                alt=""
                width="100%"
              />
            </picture>
          </StyledImageContainer>
          <StyledImageContainer mobile>
            <picture>
              <source
                srcSet={`${config.basePath}/images/hands-mobile.png`}
                type="image/png"
              />
              <StyledImg
                src={`${config.basePath}/images/hands-mobile.png`}
                alt=""
                width="100%"
              />
            </picture>
          </StyledImageContainer>
        </StyledContentBox>
      </StyledInnerContainer>
    </StyledOuterContainer>
  );
};

export default AboutBannerSection;
